/// <amd-module name="Wit/UI/PagedWorkItemsList/PagedWorkItemsList" />
import { IVssPageContext } from "VSS/Platform/Context";
import { Wiql as QueryWiql, WorkItem, WorkItemErrorPolicy, WorkItemExpand, WorkItemQueryResult } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/UI/Resources.Data";
import { isWorkItemIdType, Wiql, WorkItemId } from "../Client";
import { IPagedWorkItemsList } from "./IPagedWorkItemsList";

export enum ItemsSourceType {
    Ids,
    Wiql,
    WorkItems
}

/**
 * Default implementation of IPagedWorkItemsList
 */
export class PagedWorkItemsList implements IPagedWorkItemsList {
    private _itemsSourceType: ItemsSourceType;
    private _itemsData: (WorkItem | null)[] | null;
    private _workItemIds: WorkItemId[] | null;
    private _getQueryResultPromise: Promise<WorkItemQueryResult> | null;
    private _getWorkItemsPromises: { [key: string]: Promise<WorkItem[]> | undefined };
    private readonly _parentWorkItemCache: { [id: number]: WorkItem & { fetchError?: string } } = [];

    constructor(
        private readonly _context: IVssPageContext,
        private readonly _itemsSource: WorkItemId[] | WorkItem[] | Wiql,
        private readonly _workItemsCache: { [id: number]: WorkItem },
        private readonly _pageSize: number,
        private readonly _projectIdOrName?: string,
        private _fieldRefNames?: string[]
    ) {
        this._getWorkItemsPromises = {};

        // Make a copy of the items source so list instance won't be impacted by input changes.
        this._itemsSource = this._itemsSource.slice(0);

        // Parse the source and get its type.
        this._itemsSourceType = PagedWorkItemsList.getItemsSourceType(this._itemsSource);

        // Initialize items data
        if (this._itemsSourceType === ItemsSourceType.WorkItems) {
            // Use the items source directly if it is a work item array already.
            this._itemsData = this._itemsSource as WorkItem[];
        } else {
            this._itemsData = null;
        }

        if (_fieldRefNames) {
            this._fieldRefNames = [
                ..._fieldRefNames,
                CoreFieldRefNames.TeamProject, // Needed to generate the Work Item Type icon
                CoreFieldRefNames.WorkItemType // Needed to generate the Work Item Type icon
            ];
        }
    }

    public static getItemsSourceType(itemsSource: WorkItemId[] | WorkItem[] | Wiql): ItemsSourceType {
        if (!(itemsSource instanceof Array)) {
            return ItemsSourceType.Wiql;
        }

        if (itemsSource.length <= 0) {
            return ItemsSourceType.WorkItems;
        }

        if (isWorkItemIdType(itemsSource[0])) {
            return ItemsSourceType.Ids;
        } else {
            return ItemsSourceType.WorkItems;
        }
    }

    public isFirstPageReady(): boolean {
        return !!(this._itemsData && (this._itemsData.length === 0 || this._itemsData[0]));
    }

    public async ensureFirstPage(): Promise<void> {
        if (this._itemsSourceType === ItemsSourceType.WorkItems) {
            // All pages are already loaded if items source is work items array.
            return;
        }
        await this._pageWorkItems(0);
    }

    public async loadItemPage(itemIndex: number): Promise<void> {
        if (this._itemsSourceType === ItemsSourceType.WorkItems) {
            // All pages are already loaded if items source is work items array.
            return;
        }
        await this._pageWorkItems(itemIndex);
    }

    private async _ensureWorkItemIds(): Promise<void> {
        if (this._workItemIds) {
            return;
        }

        if (this._itemsSourceType === ItemsSourceType.Ids) {
            this._workItemIds = this._itemsSource as WorkItemId[];
        } else if (this._itemsSourceType === ItemsSourceType.Wiql) {
            if (!this._getQueryResultPromise) {
                const wiql: QueryWiql = { query: this._itemsSource as Wiql };
                const client = this._context.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
                this._getQueryResultPromise = client.queryByWiql(wiql, this._projectIdOrName);
            }

            const queryResult = await this._getQueryResultPromise;

            if (!this._workItemIds) {
                this._workItemIds = queryResult.workItems.map(item => item.id);
                this._getQueryResultPromise = null;
            }
        }

        if (this._workItemIds && !this._itemsData) {
            // Initialize items data for the first time.
            this._itemsData = [];
            this._itemsData.length = this._workItemIds.length;
            for (let i = 0; i < this._itemsData.length; i++) {
                this._itemsData[i] = null;
            }
        }
    }

    private async _pageWorkItems(itemIndex: number): Promise<void> {
        await this._ensureWorkItemIds();

        if (!this._workItemIds || !this._itemsData || this._workItemIds.length !== this._itemsData.length) {
            throw new Error(Resources.PagedWorkItemsList_NotInitialized);
        }

        const { length: totalCount } = this._workItemIds;

        if (itemIndex < 0 || (totalCount > 0 && itemIndex >= totalCount)) {
            throw new Error(Resources.PagedWorkItemsList_ItemIndexOutOfRange);
        }

        if (totalCount === 0) {
            // Query result returned no work item.
            return;
        }

        if (this._itemsData[itemIndex]) {
            // The work item data exists meaning the page has been loaded.
            return;
        }

        // Calculate start and end index of the page
        const pageSize = this._pageSize;
        const pageStart = Math.floor(itemIndex / pageSize) * pageSize;
        const pageEnd = Math.min(pageStart + pageSize, totalCount) - 1;
        const idsToFetch = this._workItemIds.filter((value, index) => {
            const notInCache = !this._workItemsCache[value];
            const indexInRange = index >= pageStart && index <= pageEnd;
            return indexInRange && notInCache;
        });
        let parentIds: number[] = [];
        let fetchedWorkItems: WorkItem[] = [];

        const promiseKey = `${pageStart}_${pageEnd}`;
        if (idsToFetch && idsToFetch.length > 0) {
            if (this._getWorkItemsPromises[promiseKey]) {
                fetchedWorkItems = await this._getWorkItemsPromises[promiseKey]!;
            } else {
                this._getWorkItemsPromises[promiseKey] = this.getWorkItemsPromise(idsToFetch);
                fetchedWorkItems = await this._getWorkItemsPromises[promiseKey]!;
                delete this._getWorkItemsPromises[promiseKey];
            }

            // Get Parent Ids from WorkItems fetched for above work item Ids
            parentIds = Array.from(new Set(fetchedWorkItems.map(x => x.fields[CoreFieldRefNames.Parent]).filter(Number)));
            // Remove parent work items which are cached.
            if (this._parentWorkItemCache) {
                parentIds = parentIds.filter(value => {
                    return !this._parentWorkItemCache[value];
                });
            }

            if (parentIds.length > 0) {
                // Get data for parent work item
                for (const id of parentIds) {
                    await this.getWorkItemPromise(id)
                        .then((result: WorkItem) => {
                            this._parentWorkItemCache[id] = result;
                        })
                        .catch((error: Error) => {
                            this._parentWorkItemCache[id] = { fetchError: error.message } as WorkItem & { fetchError?: string };
                        });
                }
            }
        }

        if (this._itemsData[itemIndex]) {
            // End now if the data has been loaded already.
            return;
        }

        // Paged data does not necessarily return in the order it's requested in. So for each
        // item we have to create a map and then put it back into the array
        // it's also possible for items requested to not be returned, in which case we will initialze
        const idToWorkItemsMap: { [key: number]: WorkItem } = {};
        fetchedWorkItems.forEach(workItem => {
            workItem.fields[CoreFieldRefNames.Parent] = this._parentWorkItemCache[workItem.fields[CoreFieldRefNames.Parent]];
            idToWorkItemsMap[workItem.id] = workItem;
        });

        for (let index = pageStart; index <= pageEnd; index++) {
            const id = this._workItemIds[index];
            const workItem = idToWorkItemsMap[id];

            if (workItem) {
                this._itemsData[index] = workItem;
                this._workItemsCache[id] = workItem;
            } else if (this._workItemsCache[id]) {
                this._itemsData[index] = this._workItemsCache[id];
            } else {
                this._itemsData[index] = null;
            }
        }
    }

    public getAllItems(): Readonly<(WorkItem | null)[]> | null {
        return this._itemsData;
    }

    private getWorkItemsPromise(workItemIds: number[]): Promise<WorkItem[]> {
        const client = this._context.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");

        return client.getWorkItems(
            workItemIds,
            this._projectIdOrName,
            this._fieldRefNames,
            undefined,
            this._fieldRefNames ? WorkItemExpand.Links : WorkItemExpand.All,
            WorkItemErrorPolicy.Omit
        );
    }

    private getWorkItemPromise(workItemId: number): Promise<WorkItem> {
        const client = this._context.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");

        return client.getWorkItem(
            workItemId,
            this._projectIdOrName,
            this._fieldRefNames,
            undefined,
            this._fieldRefNames ? WorkItemExpand.Links : WorkItemExpand.All
        );
    }
}
